import './styles.css';
import { getPlaygroundTheme } from './themes/PlaygroundEditorTheme';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useState } from 'react';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import InlineImagePlugin from './plugins/InlineImagePlugin';
import { InlineImageNode } from './nodes/InlineImageNode';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import TableCellResizer from './plugins/TableCellResizer';
import TableCellActionMenuPlugin from './plugins/TableCellActionMenuPlugin';
import Placeholder from './ui/Placeholder';
import { TableContext } from './plugins/TablePlugin';
import ContentEditable from './ui/ContentEditable';
import YouTubePlugin from './plugins/YouTubePlugin';
import { YouTubeNode } from './nodes/YouTubeNode';
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
import { $getNodeByKey } from 'lexical';
import { isJson } from '../../../utils/helper';
import { FileInfo } from '@/api/DTO/common.interface';
import { VideoNode } from './nodes/VideoNode';
import VideoPlugin from './plugins/VideoPlugin';

interface EditorDisplayProps {
	editorTheme?: 'normal' | 'style2';
	editorStateString?: string;
	images?: FileInfo[];
	isTransparentBackground?: boolean;
}

export default function EditorDisplay({
	editorTheme = 'normal',
	editorStateString,
	images,
	isTransparentBackground,
}: EditorDisplayProps) {
	// images
	const [serverRefactoredImages] = useState<FileInfo[]>(images ?? []);

	function CheckAndUpdateImagesUrlPlugIn() {
		const [editor] = useLexicalComposerContext();

		useEffect(() => {
			const removeMutationListener = editor.registerMutationListener(
				InlineImageNode,
				(mutatedNodes) => {
					for (const [nodeKey] of mutatedNodes) {
						editor.update(() => {
							const inlineImageNode = $getNodeByKey<InlineImageNode>(nodeKey);

							if (inlineImageNode) {
								const lastestFileObject = serverRefactoredImages?.find(
									(image) =>
										image.fileKey == inlineImageNode.__fileObject.fileKey //TBC
								);

								if (
									lastestFileObject &&
									lastestFileObject?.url !== inlineImageNode.__fileObject.url
								) {
									inlineImageNode?.setFileObject(lastestFileObject);
								}
							}
						});
					}
				}
			);
			return () => {
				removeMutationListener();
			};
		}, [editor]);
		return null;
	}

	function ReloadEditorStatePlugIn() {
		const [editor] = useLexicalComposerContext();

		useEffect(() => {
			setTimeout(() => {
				if (editorStateString && isJson(editorStateString)) {
					const editorState = editor.parseEditorState(editorStateString);
					editor.setEditorState(editorState);
					editor.setEditable(false);
				}
			}, 0);
		}, [editorStateString]);

		return null;
	}

	const placeholder = <Placeholder> - </Placeholder>;

	const [floatingAnchorElem, setFloatingAnchorElem] =
		useState<HTMLDivElement | null>(null);

	const onRef = (_floatingAnchorElem: HTMLDivElement) => {
		if (_floatingAnchorElem !== null) {
			setFloatingAnchorElem(_floatingAnchorElem);
		}
	};

	const editorConfig = {
		editable: false,
		...(editorStateString &&
			(isJson(editorStateString)
				? {
						editorState: editorStateString,
				  }
				: {
						editorState: null,
				  })),
		theme: getPlaygroundTheme(editorTheme),
		onError(error: Error) {
			throw error;
		},
		nodes: [
			HeadingNode,
			ListNode,
			ListItemNode,
			TableNode,
			TableCellNode,
			TableRowNode,
			AutoLinkNode,
			LinkNode,
			InlineImageNode,
			VideoNode,
			HorizontalRuleNode,
			YouTubeNode,
		],
		namespace: 'Playground',
	};

	return (
		<>
			<LexicalComposer initialConfig={editorConfig}>
				<TableContext>
					<div className='editor-shell editor-shell-read-only'>
						<div
							className={
								(isTransparentBackground
									? 'editor-container-transparent '
									: 'editor-container ') + 'tree-view'
							}
						>
							<RichTextPlugin
								contentEditable={
									<div className='editor-scroller'>
										<div className='editor' ref={onRef}>
											<ContentEditable />
										</div>
									</div>
								}
								placeholder={placeholder}
								ErrorBoundary={LexicalErrorBoundary}
							/>
							<AutoFocusPlugin />
							<ListPlugin />
							<LinkPlugin />
							<AutoLinkPlugin />
							<ListMaxIndentLevelPlugin maxDepth={7} />
							<FloatingLinkEditorPlugin />
							<HorizontalRulePlugin />
							<InlineImagePlugin />
							<VideoPlugin />
							<TablePlugin hasCellMerge={true} hasCellBackgroundColor={true} />
							<TableCellResizer />
							{floatingAnchorElem && (
								<>
									<TableCellActionMenuPlugin
										anchorElem={floatingAnchorElem}
										cellMerge={true}
									/>
								</>
							)}
							<AutoEmbedPlugin />
							<YouTubePlugin />
							<ReloadEditorStatePlugIn />
							{/* <CheckAndUpdateImagesUrlPlugIn /> */}
						</div>
					</div>
				</TableContext>
			</LexicalComposer>
		</>
	);
}
